import { ReplySolidIcon } from "@/assets/CustomIcons";
import { Avatar, Date } from "@/components";
import { AddCommentModal, ReplyToCommentModal } from "@/components/Comments";
import * as S from "@/components/Comments/styled";
import { useCommentHub } from "@/hooks";
import { useGetCommentsQuery, useGetTagsQuery } from "@/services";
import { faCirclePlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, List, Row, Typography } from "antd";
import dayjs from "dayjs";
import { bool, string } from "prop-types";
import { useState } from "react";
import { useIntl } from "react-intl";

export const CommentList = ({
  projectId,
  target,
  targetId,
  showTitle,
  editable,
}) => {
  const intl = useIntl();
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [commentId, setCommentId] = useState();

  const {
    data: commentsResponse,
    isFetching,
    refetch,
  } = useGetCommentsQuery({
    projectId,
    target,
    targetId,
  });
  useCommentHub(refetch);
  const { data: tags } = useGetTagsQuery();

  const onReplyClick = (commentId) => {
    setCommentId(commentId);
    setShowReplyModal(true);
  };

  const createCommentActions = (comment) => {
    const actions = [];
    if (editable) {
      actions.push(
        <S.ReplyButton
          size="small"
          type="link"
          data-test-id={"add-comment-reply"}
          key={"reply"}
          onClick={() => onReplyClick(comment.id)}
          icon={<ReplySolidIcon />}
        >
          {intl.formatMessage({ id: "comments.reply" })}
        </S.ReplyButton>
      );
    }

    if (comment.tag) {
      const tag = tags?.find((tag) => tag.id === comment.tag);
      if (tag) {
        actions.push(
          <S.TagWrapper>
            <S.CommentTag color={tag?.color}>{tag?.label}</S.CommentTag>
          </S.TagWrapper>
        );
      }
    }

    return actions;
  };

  return (
    <>
      <List
        header={
          <Row align="middle">
            {showTitle && (
              <Typography.Title level={4}>
                {intl.formatMessage({
                  id: "general.comments",
                })}
              </Typography.Title>
            )}
            {editable && (
              <Button
                data-test-id="intake-form-comments-add-comment"
                icon={<FontAwesomeIcon icon={faCirclePlus} />}
                type="link"
                onClick={() => setShowCommentModal(true)}
              >
                {intl.formatMessage({
                  id: "comments.addComment",
                })}
              </Button>
            )}
          </Row>
        }
        itemLayout="vertical"
        loading={isFetching}
        dataSource={commentsResponse?.comments}
        renderItem={(item) => (
          <li>
            <S.StyledComment
              author={item?.author?.displayName}
              avatar={<Avatar userId={item.author?.id} />}
              content={item?.text}
              datetime={
                <Date
                  value={dayjs(item.createdAt).toString()}
                  showTime={true}
                />
              }
              actions={createCommentActions(item)}
            >
              {!!item?.replies?.length && (
                <List
                  itemLayout="horizontal"
                  dataSource={item?.replies}
                  renderItem={(reply) => (
                    <li>
                      <S.StyledComment
                        avatar={<Avatar userId={reply.author?.id} />}
                        author={reply.author?.displayName}
                        content={reply.text}
                        datetime={
                          <Date
                            value={dayjs(reply.createdAt).toString()}
                            showTime={true}
                          />
                        }
                      />
                    </li>
                  )}
                />
              )}
            </S.StyledComment>
          </li>
        )}
      />
      <AddCommentModal
        isVisible={showCommentModal}
        target={target}
        targetId={targetId}
        projectId={projectId}
        setVisible={setShowCommentModal}
      />
      {showReplyModal && (
        <ReplyToCommentModal
          isVisible={showReplyModal}
          commentId={commentId}
          target={target}
          targetId={targetId}
          projectId={projectId}
          setVisible={setShowReplyModal}
        />
      )}
    </>
  );
};

CommentList.propTypes = {
  target: string.isRequired,
  targetId: string.isRequired,
  showTitle: bool,
  projectId: string.isRequired,
  editable: bool.isRequired,
};
